/**
 * Month-grid calendar view of synced rides.
 *
 * Pure leaf component — the parent (Dashboard) decides when to show
 * it via the view-mode toggle. Renders a 7-column grid with all the
 * days of the visible month plus leading/trailing days from adjacent
 * months to complete the first/last week. Each day cell shows up to
 * two ride badges; if a day has more rides, the third+ collapse into
 * a "+N" indicator.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRidesInRange, type RideScope } from '../hooks/useRides.ts';
import type { RideListItem } from '../types/api.ts';
import {
  formatDistanceKm,
  formatDuration,
  formatElevation,
  rideTypeChip,
} from '../lib/rideFormat.ts';
import { WeekSummary, type CalDay } from '../components/WeekSummary.tsx';
import { MonthSummary } from '../components/MonthSummary.tsx';
import { StravaSyncButton } from '../components/StravaSyncButton.tsx';
import { useStravaEnabled } from '../hooks/usePlan.ts';
import { ActivityDot, ActivityLineButton } from '../components/ActivityItems.tsx';
import { isActivity } from '../lib/sports.ts';
import { NO_FILTERS, type RideFilters } from '../hooks/useRideFilters.ts';

/** ``scope="all"`` also shows runs, swims, gym... as muted dots (no totals).
 * ``filters`` narrow the day cells only; month and week totals stay whole
 * (docs/RIDE_SOURCES_UI_PLAN.md §10.2). */
export function RidesCalendar({ scope = 'rides', filters = NO_FILTERS }: Readonly<{
  scope?: RideScope;
  filters?: RideFilters;
}>) {
  const { t } = useTranslation();
  const stravaEnabled = useStravaEnabled();
  const navigate = useNavigate();

  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const { from, to, weeks, monthLabel } = useMemo(
    () => buildMonthGrid(cursor.year, cursor.month),
    [cursor.year, cursor.month],
  );

  const { data, isLoading, isError } = useRidesInRange(from, to, scope);
  // Same request as ``data`` while no filter is on (same query key).
  const { data: shownData } = useRidesInRange(from, to, scope, filters);
  const shown = shownData ?? data;
  // Totals, badges and week summaries count rides only; other
  // activities are shown, never summed (docs/ACTIVITIES_PLAN.md §9).
  const rides = useMemo(
    () => (data?.rides ?? []).filter((r) => !isActivity(r)),
    [data],
  );
  const ridesByDate = useMemo(() => groupRidesByDate(rides), [rides]);
  const shownRidesByDate = useMemo(
    () => groupRidesByDate((shown?.rides ?? []).filter((r) => !isActivity(r))),
    [shown],
  );
  const activitiesByDate = useMemo(
    () => groupRidesByDate((shown?.rides ?? []).filter(isActivity)),
    [shown],
  );
  // Month-level rollup. Filter the fetched window to days that
  // actually belong to ``cursor.month`` so leading / trailing days
  // from adjacent months in ``weeks`` don't pollute the totals.
  const monthTotals = useMemo(() => {
    let count = 0;
    let durationSec = 0;
    let distanceM = 0;
    let tss = 0;
    let avgPowerSum = 0;
    let avgPowerN = 0;
    for (const r of rides) {
      const m = new Date(r.started_at).getMonth();
      if (m !== cursor.month) continue;
      count += 1;
      durationSec += r.duration_sec ?? 0;
      distanceM += r.distance_m ?? 0;
      tss += r.training_stress_score ?? 0;
      const w = r.normalized_power ?? r.avg_power;
      if (w != null) {
        avgPowerSum += w;
        avgPowerN += 1;
      }
    }
    return {
      count,
      hours: durationSec / 3600,
      km: distanceM / 1000,
      tss,
      avgPower: avgPowerN > 0 ? avgPowerSum / avgPowerN : null,
    };
  }, [rides, cursor.month]);

  const todayIso = new Date().toISOString().slice(0, 10);

  // When viewing the current month, drop weeks whose Monday is
  // already in the future — there are no rides in those weeks yet
  // and they otherwise sit *above* today in the reverse-chronological
  // stack, hiding the most recent activity. For past or future
  // months the user is browsing intentionally, so we don't filter.
  const isCurrentMonthView = useMemo(() => {
    const now = new Date();
    return now.getFullYear() === cursor.year && now.getMonth() === cursor.month;
  }, [cursor.year, cursor.month]);

  const visibleWeeks = useMemo(() => {
    const inMonth = weeks.filter((w) => w.some((d) => d.month === cursor.month));
    if (!isCurrentMonthView) return inMonth;
    return inMonth.filter((w) => {
      // ``w[0]`` is Monday after buildMonthGrid; a week is fully in
      // the future iff its Monday is strictly later than today.
      return w[0].iso <= todayIso;
    });
  }, [weeks, cursor.month, isCurrentMonthView, todayIso]);

  const goPrevMonth = () =>
    setCursor((c) => {
      const m = c.month - 1;
      return m < 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: m };
    });
  const goNextMonth = () =>
    setCursor((c) => {
      const m = c.month + 1;
      return m > 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: m };
    });
  const goToday = () => {
    const now = new Date();
    setCursor({ year: now.getFullYear(), month: now.getMonth() });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-3 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={goPrevMonth}
            className="px-3 py-1 rounded-lg text-sm border border-(--color-border) text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-tertiary) transition-colors"
            aria-label={t('dashboard.calendar.prevMonth')}
          >
            ←
          </button>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-(--color-text-primary)">
              {monthLabel}
            </h3>
            <button
              onClick={goToday}
              className="text-xs text-(--color-text-secondary) hover:text-(--color-accent) underline"
            >
              {t('dashboard.calendar.today')}
            </button>
            {stravaEnabled && <StravaSyncButton />}
          </div>
          <button
            onClick={goNextMonth}
            className="px-3 py-1 rounded-lg text-sm border border-(--color-border) text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-tertiary) transition-colors"
            aria-label={t('dashboard.calendar.nextMonth')}
          >
            →
          </button>
        </div>
        {/* Month-level totals so the user gets a "this month at a
            glance" reading without scrolling the grid. Hidden when
            the visible month has no rides — printing zeroes is
            visually noisy. */}
        {monthTotals.count > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-(--color-text-secondary) border-t border-(--color-border)/60 pt-2">
            <span className="font-medium text-(--color-text-primary)">
              {monthTotals.count} {monthTotals.count === 1 ? 'ride' : 'rides'}
            </span>
            <span>·</span>
            <span>{monthTotals.hours.toFixed(1)}h</span>
            <span>·</span>
            <span>{monthTotals.km.toFixed(0)} km</span>
            <span>·</span>
            <span>TSS {Math.round(monthTotals.tss)}</span>
            {monthTotals.avgPower != null && (
              <>
                <span>·</span>
                <span>{t('dashboard.calendar.avgPower')} {Math.round(monthTotals.avgPower)}W</span>
              </>
            )}
          </div>
        )}
        <MonthSummary
          year={cursor.year}
          month={cursor.month}
          monthRideCount={monthTotals.count}
        />
      </div>

      {isError && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 text-sm">
          {t('dashboard.calendar.errorLoading')}
        </div>
      )}

      {/* Desktop / tablet — 7-col month grid. Hidden on phones where
          the columns get cramped; mobile gets the day-row list below. */}
      <div className="hidden sm:grid grid-cols-7 gap-1 text-[10px] text-(--color-text-secondary) uppercase tracking-wide text-center">
        {WEEKDAY_HEADERS.map((d) => (
          <div key={d} className="py-1">
            {t(`dashboard.calendar.weekday.${d}`)}
          </div>
        ))}
      </div>

      <div className={`hidden sm:flex flex-col gap-1 ${isLoading ? 'opacity-50' : ''}`}>
        {/* Reverse chronological — today's week sits at the top, past
            weeks below. For the current month, weeks fully in the
            future are filtered out (see visibleWeeks). Cross-month
            padding inside a retained week renders as empty
            placeholders so the 7-col grid alignment stays correct. */}
        {[...visibleWeeks]
          .reverse()
          .map((week) => (
            // ``week[0]`` is the week's Monday — unique per row.
            <div key={week[0].iso} className="flex flex-col gap-1">
              {/* WeekSummary is the header for the week's day grid —
                  matches the mobile agenda layout where the summary
                  also leads each Mon-Sun chunk. */}
              <WeekSummary week={week} ridesByDate={ridesByDate} />
              <div className="grid grid-cols-7 gap-1">
                {week.map((day) =>
                  day.month === cursor.month ? (
                    <DayCell
                      key={day.iso}
                      day={day}
                      isCurrentMonth
                      isToday={day.iso === todayIso}
                      rides={shownRidesByDate.get(day.iso) ?? []}
                      activities={activitiesByDate.get(day.iso) ?? []}
                      onRideClick={(id) => navigate(`/rides/${id}`)}
                    />
                  ) : (
                    <div key={day.iso} aria-hidden="true" />
                  ),
                )}
              </div>
            </div>
          ))}
      </div>

      {/* Mobile — agenda-style list of full-width day rows, intervals.icu
          inspired. Shows only the current month's days, reverse
          chronological so today / most-recent past sit at the top.
          Future-dated days are filtered out: they can never have a
          ride attached and would just push real activity below the
          fold (the bug that prompted this layout — May 31 was
          appearing above May 9 on the user's phone). Per-week
          summary strips group runs of days so mobile gets the same
          Mon-Sun rollups desktop already has. */}
      <div className={`sm:hidden flex flex-col gap-2 ${isLoading ? 'opacity-50' : ''}`}>
        {[...visibleWeeks].reverse().map((week) => {
          // For the current month view, also hide future days inside
          // today's row (May 10 when today is May 9). For past or
          // future months the user is browsing intentionally so all
          // days within the visible month show.
          const weekDays = week.filter((d) => {
            if (d.month !== cursor.month) return false;
            return !isCurrentMonthView || d.iso <= todayIso;
          });
          if (weekDays.length === 0) return null;
          return (
            <div key={week[0].iso} className="flex flex-col gap-2">
              <WeekSummary week={week} ridesByDate={ridesByDate} />
              {[...weekDays].reverse().map((day) => (
                <MobileDayRow
                  key={day.iso}
                  day={day}
                  isToday={day.iso === todayIso}
                  rides={shownRidesByDate.get(day.iso) ?? []}
                  activities={activitiesByDate.get(day.iso) ?? []}
                  onRideClick={(id) => navigate(`/rides/${id}`)}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MobileDayRow({
  day,
  isToday,
  rides,
  activities,
  onRideClick,
}: Readonly<{
  day: CalDay;
  isToday: boolean;
  rides: RideListItem[];
  activities: RideListItem[];
  onRideClick: (id: number) => void;
}>) {
  const date = new Date(day.iso);
  const weekday = date.toLocaleDateString(undefined, { weekday: 'short' });

  // Empty day — thin strip so users can scroll the month and see
  // the cadence (rest days included). Today still gets the accent
  // ring so it's locatable.
  if (rides.length === 0 && activities.length === 0) {
    return (
      <div
        className={`flex items-center gap-3 py-1.5 px-2 rounded-md text-(--color-text-secondary) text-xs ${
          isToday
            ? 'ring-1 ring-(--color-accent) bg-(--color-bg-secondary)'
            : ''
        }`}
      >
        <span
          className={`shrink-0 w-12 ${
            isToday ? 'text-(--color-accent) font-medium' : ''
          }`}
        >
          {day.day} {weekday}
        </span>
        <span className="opacity-50">{'—'}</span>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border bg-(--color-bg-secondary) ${
        isToday
          ? 'border-(--color-accent)'
          : 'border-(--color-border)'
      }`}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-(--color-border)/60">
        <span
          className={`text-sm font-medium ${
            isToday
              ? 'text-(--color-accent)'
              : 'text-(--color-text-primary)'
          }`}
        >
          {day.day} {weekday}
        </span>
        {rides.length > 1 && <DayTotalBadge rides={rides} />}
      </div>
      <div className="flex flex-col gap-1.5 p-2">
        {rides.map((ride) => (
          <RideBadge
            key={ride.id}
            ride={ride}
            onClick={() => onRideClick(ride.id)}
            variant="expanded"
          />
        ))}
        {activities.map((a) => (
          <ActivityLineButton key={a.id} item={a} onClick={() => onRideClick(a.id)} />
        ))}
      </div>
    </div>
  );
}

function DayCell({
  day,
  isCurrentMonth,
  isToday,
  rides,
  activities,
  onRideClick,
}: Readonly<{
  day: CalDay;
  isCurrentMonth: boolean;
  isToday: boolean;
  rides: RideListItem[];
  activities: RideListItem[];
  onRideClick: (id: number) => void;
}>) {
  const hasRides = rides.length > 0;
  // Two rides per cell on phone (richer pills, less room), up to
  // three on tablet+. Past that collapses into "+N" overflow.
  const visibleMobile = 2;
  const visibleDesktop = 3;
  const visible = rides.slice(0, visibleDesktop);
  const overflowMobile = Math.max(0, rides.length - visibleMobile);
  const overflowDesktop = Math.max(0, rides.length - visibleDesktop);

  return (
    <div
      className={`min-h-24 sm:min-h-44 rounded-lg border flex flex-col gap-1 sm:gap-1.5 p-1 sm:p-2.5 ${
        isCurrentMonth
          ? 'bg-(--color-bg-secondary) border-(--color-border)'
          : 'bg-(--color-bg-tertiary)/40 border-(--color-border)/40'
      } ${isToday ? 'ring-1 ring-(--color-accent)' : ''}`}
    >
      <div className="flex items-baseline justify-between gap-1">
        <div
          className={`text-[10px] sm:text-sm font-medium leading-none ${
            isCurrentMonth
              ? 'text-(--color-text-primary)'
              : 'text-(--color-text-secondary)'
          } ${isToday ? 'text-(--color-accent)' : ''}`}
        >
          {day.day}
        </div>
        {/* Per-day total — only meaningful when 2+ rides stack. */}
        {rides.length > 1 && <DayTotalBadge rides={rides} />}
      </div>

      {hasRides && (
        <div className="flex flex-col gap-0.5 sm:gap-1.5 min-w-0">
          {/* Mobile: render only the first ``visibleMobile`` so cell
              height stays sane. Desktop: render up to
              ``visibleDesktop``. The pills past ``visibleMobile`` use
              ``hidden sm:block`` so they appear at the breakpoint. */}
          {visible.map((r, i) => (
            <div
              key={r.id}
              className={i < visibleMobile ? '' : 'hidden sm:block'}
            >
              <RideBadge ride={r} onClick={() => onRideClick(r.id)} />
            </div>
          ))}
          {overflowMobile > 0 && (
            <span className="sm:hidden text-[9px] text-(--color-text-secondary) px-1">
              +{overflowMobile}
            </span>
          )}
          {overflowDesktop > 0 && (
            <span className="hidden sm:inline text-[10px] text-(--color-text-secondary) px-1">
              +{overflowDesktop}
            </span>
          )}
        </div>
      )}
      {activities.length > 0 && (
        <div className="flex flex-wrap gap-0.5">
          {activities.map((a) => (
            <ActivityDot key={a.id} item={a} onClick={() => onRideClick(a.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function DayTotalBadge({ rides }: Readonly<{ rides: RideListItem[] }>) {
  const totals = useMemo(() => {
    let durationSec = 0;
    let tss = 0;
    for (const r of rides) {
      durationSec += r.duration_sec ?? 0;
      tss += r.training_stress_score ?? 0;
    }
    return { durationSec, tss };
  }, [rides]);

  return (
    <span
      className="text-[9px] sm:text-[10px] text-(--color-text-secondary) shrink-0"
      title={`${rides.length} rides total`}
    >
      Σ {Math.round(totals.tss)} · {(totals.durationSec / 3600).toFixed(1)}h
    </span>
  );
}


type RideBadgeVariant = 'compact' | 'expanded';

function rideBadgeTitle(
  ride: RideListItem,
  duration: string,
  distance: string,
  tss: number | null,
): string {
  const np = ride.normalized_power;
  const ifVal = ride.intensity_factor;
  const hr = ride.avg_hr;
  const tssPart = tss == null ? '' : `, TSS ${tss}`;
  const npPart = np == null ? '' : `, NP ${np}W`;
  const hrPart = hr == null ? '' : `, HR ${hr}bpm`;
  const ifPart = ifVal == null ? '' : `, IF ${ifVal.toFixed(2)}`;
  const source = ride.attribution ? ` (${ride.attribution})` : '';
  return `${ride.name ?? 'Ride'}${source} — ${duration}, ${distance}${tssPart}${npPart}${hrPart}${ifPart}`;
}

/** The title; Garmin rides name their source next to it (brand guidelines). */
function rideBadgeTitleText(ride: RideListItem): string | null {
  return ride.attribution ? `${ride.name ?? 'Ride'} · ${ride.attribution}` : ride.name ?? null;
}

/** NP, IF, heart rate and climbing, when the ride has them. */
function rideBadgeSubtitle(ride: RideListItem): string[] {
  const parts: string[] = [];
  if (ride.normalized_power != null) parts.push(`${ride.normalized_power}W`);
  if (ride.intensity_factor != null) parts.push(`IF ${ride.intensity_factor.toFixed(2)}`);
  if (ride.avg_hr != null) parts.push(`${ride.avg_hr}bpm`);
  const elev = ride.elevation_gain_m;
  if (elev != null && elev > 0) parts.push(formatElevation(elev));
  return parts;
}

function RideBadge({
  ride,
  onClick,
  variant = 'compact',
}: Readonly<{
  ride: RideListItem;
  onClick: () => void;
  variant?: RideBadgeVariant;
}>) {
  const chip = rideTypeChip(ride.ride_type);
  const tss =
    ride.training_stress_score == null
      ? null
      : Math.round(ride.training_stress_score);
  const duration = formatDuration(ride.duration_sec);
  const distance = formatDistanceKm(ride.distance_m);
  const sportIcon = ride.is_indoor ? '\u{1F3E0}' : '\u{1F6B4}';
  const hasCoach = !!ride.coach_summary;
  const titleText = rideBadgeTitleText(ride);
  const subtitleParts = rideBadgeSubtitle(ride);

  // Variant guides info density:
  //  - "compact" = desktop month-grid cell (cramped, tablet-up only).
  //    Title row appears at sm+ via the existing breakpoint hack.
  //  - "expanded" = mobile day-row layout (full container width, lots
  //    of room). Title and subtitle always visible regardless of
  //    breakpoint.
  const expanded = variant === 'expanded';

  return (
    <button
      onClick={onClick}
      title={rideBadgeTitle(ride, duration, distance, tss)}
      className={`block w-full text-left rounded border-l-4 ${chip.className} ${
        expanded ? 'px-2 py-1.5' : 'px-1.5 py-0.5 sm:px-2 sm:py-1.5'
      } leading-tight overflow-hidden`}
    >
      {titleText && (
        <div
          className={`flex items-center justify-between gap-1 min-w-0 text-[10px] sm:text-xs opacity-75 mb-0.5 ${
            expanded ? '' : 'hidden sm:flex'
          }`}
        >
          <span className="truncate italic">{titleText}</span>
          {hasCoach && (
            <span className="shrink-0" title="Coach summary available">
              {'\u{1F916}'}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-1 min-w-0">
        <span className={`${expanded ? 'text-sm' : 'text-[10px] sm:text-sm'} font-bold truncate`}>
          {tss == null ? '·' : `${tss} TSS`}
        </span>
        <span
          className={`${expanded ? 'text-xs' : 'text-[9px] sm:text-xs'} opacity-75 shrink-0`}
        >
          {sportIcon} {duration}
        </span>
      </div>
      <div
        className={`flex items-center justify-between gap-1 min-w-0 ${
          expanded ? 'text-xs' : 'text-[9px] sm:text-xs'
        } opacity-70`}
      >
        <span className="truncate">{distance}</span>
        {!titleText && hasCoach && (
          <span
            className={`shrink-0 ${expanded ? '' : 'sm:hidden'}`}
            title="Coach summary available"
          >
            {'\u{1F916}'}
          </span>
        )}
      </div>
      {subtitleParts.length > 0 && (
        <div
          className={`${
            expanded
              ? 'text-xs opacity-65'
              : 'hidden sm:block text-[11px] opacity-65'
          } truncate`}
        >
          {subtitleParts.join(' · ')}
        </div>
      )}
    </button>
  );
}

const WEEKDAY_HEADERS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

function buildMonthGrid(year: number, month: number): {
  from: string;
  to: string;
  weeks: CalDay[][];
  monthLabel: string;
} {
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  // Grid starts on the Monday on/before the 1st.
  // getDay(): 0=Sun..6=Sat. Convert to Mon-first: (d+6)%7.
  const startOffset = (firstOfMonth.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - startOffset);

  // Always render 6 weeks (42 cells) so the grid height doesn't jump
  // between months — common UX choice for month calendars.
  const days: CalDay[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(
      gridStart.getFullYear(),
      gridStart.getMonth(),
      gridStart.getDate() + i,
    );
    days.push({
      iso: toIsoDate(d),
      day: d.getDate(),
      month: d.getMonth(),
    });
  }

  const weeks: CalDay[][] = [];
  for (let i = 0; i < 6; i++) {
    weeks.push(days.slice(i * 7, i * 7 + 7));
  }

  const monthLabel = firstOfMonth.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return {
    from: toIsoDate(firstOfMonth),
    to: toIsoDate(lastOfMonth),
    weeks,
    monthLabel,
  };
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function groupRidesByDate(rides: RideListItem[]): Map<string, RideListItem[]> {
  const out = new Map<string, RideListItem[]>();
  for (const r of rides) {
    const day = r.started_at.slice(0, 10);
    const list = out.get(day) ?? [];
    list.push(r);
    out.set(day, list);
  }
  return out;
}
